import { useEffect, useState } from 'preact/hooks';
import { api, mensagemDoErro } from '../api';
import { inteiro, plural } from '../format';
import { useApi } from '../hooks';
import type { CargaInfo, FonteDadosInfo } from '../types';
import { Carregando, ErroForm, Selo, avisar, confirmar } from '../ui';
import { dataHora, useRecarga } from './comum';
import { ImportarDadosModal } from './ImportarDadosModal';

type CargaComFonte = CargaInfo & { fonte: string; fonteNome: string };

const STATUS: Record<CargaInfo['status'], { texto: string; tom: 'verde' | 'ambar' | 'vermelho' | 'azul' }> = {
  EXECUTANDO: { texto: 'Atualizando…', tom: 'azul' },
  OK: { texto: 'Em dia', tom: 'verde' },
  PARCIAL: { texto: 'Parcial', tom: 'ambar' },
  ERRO: { texto: 'Falhou', tom: 'vermelho' },
};

const origem = (por: string) => (por === 'agenda' ? 'Automática' : por === 'cli' ? 'Linha de comando' : 'Manual');

export function DadosOficiaisPage() {
  const [recarga, recarregar] = useRecarga();
  const fontes = useApi<FonteDadosInfo[]>('/dados-oficiais/fontes', recarga);
  const cargas = useApi<CargaComFonte[]>('/dados-oficiais/cargas?limite=15', recarga);
  const [enviando, setEnviando] = useState<FonteDadosInfo | null>(null);

  // Enquanto alguma fonte está sendo atualizada, confere de 3 em 3 segundos até terminar.
  const rodando = fontes.dados?.some((f) => f.executando) ?? false;
  useEffect(() => {
    if (!rodando) return;
    const t = setInterval(recarregar, 3000);
    return () => clearInterval(t);
  }, [rodando]);

  async function atualizar(slug: string) {
    try {
      await api(`/dados-oficiais/fontes/${slug}/carregar`, { method: 'POST' });
      avisar('Atualização iniciada. Pode levar alguns instantes.');
      recarregar();
    } catch (e) {
      avisar(mensagemDoErro(e), 'erro');
    }
  }

  async function remover(f: FonteDadosInfo, indicador: string, periodo: string, nome: string) {
    if (!(await confirmar(`Apagar os valores de ${nome} de ${periodo}? Os números deixam de aparecer no painel. Dá para enviar a planilha de novo depois.`, { confirmar: 'Apagar', perigo: true }))) return;
    try {
      const r = await api<{ removidos: number }>(`/dados-oficiais/fontes/${f.slug}/valores?periodo=${periodo}&indicador=${indicador}`, { method: 'DELETE' });
      avisar(`${plural(r.removidos, 'valor apagado', 'valores apagados')}.`);
      recarregar();
    } catch (e) {
      avisar(mensagemDoErro(e), 'erro');
    }
  }

  return (
    <main class="pagina">
      <header class="pagina-topo">
        <div>
          <h1>Dados oficiais</h1>
          <p class="dica">
            IBGE, Saúde e Tesouro vêm direto das APIs dos órgãos: <strong>ninguém precisa baixar planilha</strong>, cada fonte se atualiza sozinha e aqui você acompanha ou atualiza na hora.
            O <strong>IDEB</strong> e a <strong>segurança pública</strong> não têm API: os dados entram por <strong>planilha enviada aqui</strong>, validada antes de gravar.
          </p>
        </div>
      </header>

      {fontes.erro && <ErroForm texto={mensagemDoErro(fontes.erro)} />}
      {fontes.carregando && !fontes.dados && <Carregando />}

      <section class="fontes" aria-label="Fontes de dados">
        {(fontes.dados ?? []).map((f) => {
          const c = f.ultimaCarga;
          const arquivo = f.tipoAcesso === 'ARQUIVO';
          const st = f.executando ? STATUS.EXECUTANDO : c ? STATUS[c.status] : null;
          return (
            <article key={f.slug} class="fonte">
              <header>
                <div>
                  <h2>{f.nome}</h2>
                  <p class="dica">{f.orgao} · <a href={f.url} target="_blank" rel="noopener noreferrer">{f.url.replace('https://', '')}</a></p>
                </div>
                <div class="chips">
                  {arquivo && f.valoresGuardados === 0 && <Selo texto="Aguardando planilha" tom="ambar" />}
                  {st && !(arquivo && f.valoresGuardados === 0) && <Selo texto={arquivo && st.texto === 'Em dia' ? 'Com dados' : st.texto} tom={st.tom} />}
                  {f.vencida && !f.executando && <Selo texto="Desatualizada" tom="ambar" />}
                </div>
              </header>
              <dl class="fonte-dados">
                <div><dt>Indicadores</dt><dd>{f.indicadores}</dd></div>
                <div><dt>Valores guardados</dt><dd>{inteiro(f.valoresGuardados)}</dd></div>
                <div><dt>Última atualização</dt><dd>{f.ultimaBemSucedida ? dataHora(f.ultimaBemSucedida) : 'nunca'}</dd></div>
                {arquivo ? (
                  <div><dt>Nova edição do órgão</dt><dd>a cada {f.periodicidadeDias >= 365 ? plural(Math.round(f.periodicidadeDias / 365), 'ano', 'anos') : `${f.periodicidadeDias} dias`}</dd></div>
                ) : (
                  <>
                    <div><dt>Próxima automática</dt><dd>{f.proximaPrevista ? dataHora(f.proximaPrevista) : 'assim que possível'}</dd></div>
                    <div><dt>Frequência</dt><dd>a cada {f.periodicidadeDias} dias</dd></div>
                  </>
                )}
                <div><dt>Acesso</dt><dd>{arquivo ? 'Planilha enviada aqui' : 'API aberta'}{f.exigeChave ? ' (exige chave)' : ' · sem cadastro'}</dd></div>
              </dl>
              {c && (c.status === 'PARCIAL' || c.status === 'ERRO') && c.mensagem && <p class={c.status === 'ERRO' ? 'erro caixa-erro' : 'aviso'}>{c.mensagem}</p>}
              {arquivo && (f.periodosEnviados?.length ?? 0) > 0 && (
                <ul class="lista-periodos" aria-label="Anos enviados">
                  {f.periodosEnviados!.map((p) => {
                    const nome = f.indicadoresDaFonte?.find((i) => i.slug === p.indicador)?.nome ?? p.indicador;
                    return (
                      <li key={p.indicador + p.periodo}>
                        <span><strong>{p.periodo}</strong> · {nome} <small class="sub">{plural(p.municipios, 'município', 'municípios')} · enviado em {dataHora(p.atualizadoEm)}</small></span>
                        <button type="button" class="botao pequeno perigo" onClick={() => void remover(f, p.indicador, p.periodo, nome)}>Apagar</button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div class="modal-acoes">
                {arquivo ? (
                  <button type="button" class="botao primario" onClick={() => setEnviando(f)}>Enviar planilha</button>
                ) : (
                  <button type="button" class="botao" disabled={f.executando} onClick={() => void atualizar(f.slug)}>{f.executando ? 'Atualizando…' : 'Atualizar agora'}</button>
                )}
              </div>
            </article>
          );
        })}
      </section>

      <section class="bloco">
        <h2>Histórico de atualizações</h2>
        {cargas.carregando && !cargas.dados && <Carregando />}
        <div class="tabela-rolagem">
          <table class="tabela">
            <caption class="so-leitor">Últimas atualizações</caption>
            <thead><tr><th scope="col">Quando</th><th scope="col">Fonte</th><th scope="col">Situação</th><th scope="col" class="num">Lidos</th><th scope="col" class="num">Gravados</th><th scope="col" class="num">Descartados</th><th scope="col">Como</th></tr></thead>
            <tbody>
              {(cargas.dados ?? []).map((c) => (
                <tr key={c.id}>
                  <td>{dataHora(c.iniciadaEm)}</td>
                  <td>{c.fonte.toUpperCase()}</td>
                  <td><Selo texto={STATUS[c.status].texto} tom={STATUS[c.status].tom} /></td>
                  <td class="num">{inteiro(c.lidas)}</td>
                  <td class="num">{inteiro(c.gravadas)}</td>
                  <td class="num">{inteiro(c.descartadas)}</td>
                  <td>{origem(c.disparadaPor)}{c.mensagem && <small class="sub">{c.mensagem}</small>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {cargas.dados?.length === 0 && <p class="vazio-grande">Nenhuma atualização ainda. A primeira acontece sozinha logo após o sistema subir.</p>}
        </div>
      </section>
      {enviando && <ImportarDadosModal fonte={enviando} onFechar={() => setEnviando(null)} onImportado={recarregar} />}
    </main>
  );
}
